import { motion } from "framer-motion";
import { useState } from "react";
import { Check, Sparkles, Crown, Rocket } from "lucide-react";
import CheckoutDialog from "./CheckoutDialog";

const PLANS = [
  {
    id: "start",
    icon: Rocket,
    name: "Start",
    oldPrice: "R$ 1.500",
    price: "R$ 1.000",
    period: "/mês",
    tagline: "Ideal para empresas que estão começando a anunciar.",
    note: "*Investimento dos anúncios pagos à parte.",
    features: [
      "Gestão de Tráfego Pago",
      "Criativos básicos para anúncios",
      "Reels básicos",
      "Acompanhamento básico",
      "Suporte via WhatsApp",
    ],
    cta: "Quero este plano",
    featured: false,
  },
  {
    id: "pro",
    icon: Sparkles,
    name: "Pro",
    oldPrice: "R$ 3.000",
    price: "R$ 2.500",
    period: "/mês",
    tagline: "Ideal para marcas que desejam crescimento e posicionamento profissional.",
    note: "*Investimento dos anúncios pagos à parte.",
    features: [
      "Gestão de Tráfego Pago",
      "Criativos estratégicos",
      "Reels estratégicos",
      "Planejamento de conteúdo",
      "Acompanhamento mensal",
      "Direcionamento estratégico",
      "Organização visual do perfil",
      "Suporte prioritário",
    ],
    cta: "Quero este plano",
    featured: true,
    badge: "★ MAIS ESCOLHIDO",
  },
  {
    id: "elite",
    icon: Crown,
    name: "Elite",
    oldPrice: "R$ 4.800",
    price: "R$ 4.000",
    period: "/mês",
    tagline: "Ideal para empresas que desejam presença forte, autoridade e crescimento acelerado.",
    features: [
      "Social Media completo",
      "Gestão de Tráfego Pago",
      "Criativos profissionais",
      "Estratégia completa de posicionamento",
      "Reels profissionais",
      "Planejamento estratégico mensal",
      "Branding",
      "Posicionamento de marca",
      "Acompanhamento estratégico completo",
      "Análise de métricas",
      "Otimização do perfil",
      "Reuniões de alinhamento",
      "Suporte prioritário",
    ],
    cta: "Solicitar Proposta",
    featured: false,
    badge: "♛ PLANO PREMIUM",
  },
];

function PlanCard({ p, i, onContratar }) {
  const Icon = p.icon;
  const isPro = p.featured;

  return (
    <motion.article
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay: 0.1 * i, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -8 }}
      data-testid={`plan-card-${p.id}`}
      className={`relative flex flex-col rounded-3xl backdrop-blur-2xl transition-all duration-500 group
        ${isPro
          ? "bg-gradient-to-b from-green-500/[0.10] via-green-500/[0.04] to-white/[0.02] border-2 border-green-500/70 shadow-[0_0_80px_-10px_rgba(34,197,94,0.5),inset_0_1px_0_rgba(255,255,255,0.08)] lg:scale-[1.06] lg:-mt-4 z-10 p-10 md:p-12"
          : "bg-white/[0.03] border border-white/[0.08] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.05)] hover:border-blue-400/40 hover:bg-white/[0.05] p-8 md:p-10"}
      `}
    >
      {/* Badge */}
      {p.badge && (
        <div
          data-testid={`plan-badge-${p.id}`}
          className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-[10px] uppercase tracking-[0.28em] font-semibold whitespace-nowrap
            ${isPro
              ? "bg-green-500 text-white shadow-[0_0_30px_rgba(34,197,94,0.6)]"
              : "bg-gradient-to-r from-blue-300 to-blue-500 text-ink-950 shadow-[0_0_25px_rgba(107,138,255,0.4)]"}
          `}
        >
          {p.badge}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center backdrop-blur-md ${
          isPro ? "bg-green-500/20 ring-1 ring-green-500/50" : "bg-white/[0.05] ring-1 ring-white/10"
        }`}>
          <Icon className={`w-5 h-5 ${isPro ? "text-green-400" : "text-blue-soft"}`} strokeWidth={1.5} />
        </div>
        <h3 className="font-serif text-2xl md:text-3xl text-white tracking-tight font-semibold">
          Plano {p.name}
        </h3>
      </div>

      {/* Price */}
      <div className="mb-2">
        {p.oldPrice && (
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/15 border border-green-500/40 text-green-400 text-[9px] uppercase tracking-[0.22em] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              Oferta por tempo limitado
            </span>
          </div>
        )}
        {p.oldPrice && (
          <div className="text-white/40 text-lg md:text-xl line-through decoration-white/40 decoration-2 mb-0.5">
            {p.oldPrice}
          </div>
        )}
        <div className="flex items-baseline gap-2">
          <span className="font-serif font-bold tracking-tight text-white text-5xl md:text-6xl">
            {p.price}
          </span>
          {p.period && <span className="text-white/50 text-sm md:text-base">{p.period}</span>}
        </div>
        {p.note && (
          <p className="mt-2 text-[11px] text-white/40 italic">{p.note}</p>
        )}
      </div>

      {/* Tagline */}
      <p className="mt-6 text-white/60 text-sm leading-relaxed pb-8 border-b border-white/10">
        {p.tagline}
      </p>

      {/* Features */}
      <ul className="mt-8 space-y-3.5 flex-1">
        {p.features.map((f, k) => (
          <li key={k} className="flex items-start gap-3 text-white/80 text-sm leading-relaxed">
            <span className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${
              isPro ? "bg-green-500/20" : "bg-white/[0.06]"
            }`}>
              <Check className={`w-3 h-3 ${isPro ? "text-green-400" : "text-blue-soft"}`} strokeWidth={2.5} />
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      <button
        type="button"
        data-testid={`plan-cta-${p.id}`}
        onClick={() => onContratar(p)}
        className="mt-10 relative overflow-hidden inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-4 text-xs uppercase tracking-[0.22em] font-bold text-white bg-[#22C55E] hover:bg-[#16A34A] shadow-[0_10px_30px_-8px_rgba(34,197,94,0.55),inset_0_1px_0_rgba(255,255,255,0.15)] hover:shadow-[0_18px_40px_-6px_rgba(34,197,94,0.75),inset_0_1px_0_rgba(255,255,255,0.2)] transition-all duration-300 hover:scale-[1.04] active:scale-[0.98]"
      >
        <span className="relative z-10">Contratar Agora</span>
        <span className="relative z-10 transition-transform duration-500 group-hover:translate-x-1">→</span>
      </button>
    </motion.article>
  );
}

export default function Services() {
  const [selected, setSelected] = useState(null);

  return (
    <section
      id="servicos"
      data-testid="services-section"
      className="relative py-32 md:py-48 overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse 90% 60% at 50% 0%, rgba(10,77,255,0.28), transparent 60%), linear-gradient(180deg, #050505 0%, #030817 40%, #061436 75%, #0A2668 100%)",
      }}
    >
      {/* Glow orbs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 -left-40 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,_rgba(10,77,255,0.35),_transparent_60%)] blur-3xl" />
        <div className="absolute bottom-0 -right-40 w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,_rgba(74,124,255,0.25),_transparent_60%)] blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[radial-gradient(circle,_rgba(107,138,255,0.15),_transparent_70%)] blur-3xl" />
      </div>

      {/* Grid backdrop */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #6B8AFF 1px, transparent 1px), linear-gradient(to bottom, #6B8AFF 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
      />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12">
        {/* Overline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="flex items-center justify-center gap-3 text-[10px] uppercase tracking-[0.35em] text-blue-soft mb-8"
        >
          <span className="w-8 h-px bg-blue-soft/60" />
          <span>Nossos planos</span>
          <span className="w-8 h-px bg-blue-soft/60" />
        </motion.div>

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1 }}
          className="font-serif font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-white text-center max-w-4xl mx-auto"
        >
          Escolha o plano ideal para{" "}
          <span className="bg-gradient-to-r from-blue-300 via-blue-soft to-white bg-clip-text text-transparent">
            acelerar o crescimento
          </span>{" "}
          da sua empresa.
        </motion.h2>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.2 }}
          className="mt-6 text-white/60 text-base md:text-lg leading-relaxed font-light text-center max-w-2xl mx-auto"
        >
          Planos desenvolvidos para empresas que desejam vender mais, fortalecer sua marca e crescer através do marketing digital.
        </motion.p>

        {/* Cards grid */}
        <div className="mt-20 md:mt-28 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-6 items-start">
          {PLANS.map((p, i) => (
            <PlanCard key={p.id} p={p} i={i} onContratar={setSelected} />
          ))}
        </div>

        {/* Fine print */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.4 }}
          className="mt-16 text-center text-white/40 text-xs tracking-wide"
        >
          Todos os planos incluem contrato mensal sem fidelidade. Cancele quando quiser.
        </motion.p>
      </div>

      <CheckoutDialog
        open={!!selected}
        onClose={() => setSelected(null)}
        planId={selected?.id}
        planName={selected ? `Plano ${selected.name}` : ""}
        priceLabel={selected ? `${selected.price}${selected.period || ""}` : ""}
      />
    </section>
  );
}
